import React from "react";
import { motion, AnimatePresence } from "framer-motion";

const NAV_LINKS = [
    { key: 0, label: "Home", href: "#main" },
    { key: 1, label: "Projects", href: "#projectRoot" },
    { key: 2, label: "Experience", href: "#experienceRoot" },
    { key: 3, label: "Skills", href: "#techStackRoot" },
    { key: 4, label: "About", href: "#aboutRoot" },
    { key: 5, label: "Contact", href: "#contactMe" },
];

function SunIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
    );
}

function MoonIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 1020.354 15.354z" />
        </svg>
    );
}

export default function Navbar() {
    const [clickedPage, setClickedPage] = React.useState(0);
    const [menuOpen, setMenuOpen] = React.useState(false);
    const [theme, setTheme] = React.useState(() => {
        if (typeof window === "undefined") return "dark";
        return window.localStorage.getItem("theme") || "dark";
    });
    // Suppresses the scroll-spy observer for a moment after a manual nav
    // click, so the just-clicked link doesn't flicker before the smooth
    // scroll actually arrives at the target section.
    const suppressSpyRef = React.useRef(false);

    React.useEffect(() => {
        document.documentElement.setAttribute("data-theme", theme);
        window.localStorage.setItem("theme", theme);
    }, [theme]);

    // Scroll-spy: highlight whichever section is currently in view, not
    // just whichever link was last clicked.
    React.useEffect(() => {
        const sections = NAV_LINKS
            .map((link) => ({ key: link.key, el: document.querySelector(link.href) }))
            .filter((entry) => entry.el);

        if (!sections.length) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                if (suppressSpyRef.current) return;
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const match = sections.find((s) => s.el === entry.target);
                        if (match) setClickedPage(match.key);
                    }
                });
            },
            { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
        );

        sections.forEach((s) => observer.observe(s.el));
        return () => observer.disconnect();
    }, []);

    function toggleTheme() {
        setTheme((prev) => (prev === "dark" ? "light" : "dark"));
    }

    function handleClick(event, key, href) {
        event.preventDefault();
        setClickedPage(key);
        setMenuOpen(false);
        suppressSpyRef.current = true;
        const target = document.querySelector(href);
        if (target) {
            window.scrollTo({ top: target.offsetTop, behavior: "smooth" });
        }
        window.setTimeout(() => {
            suppressSpyRef.current = false;
        }, 1000);
    }

    return (
        <nav>
            <div id="navInner">
                <a href="#main" id="navBrand" className="link" onClick={(e) => handleClick(e, 0, "#main")}>
                    Shashank<span>.</span>dev
                </a>

                <button
                    id="menuToggle"
                    aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
                    aria-expanded={menuOpen}
                    aria-controls="navLinks"
                    onClick={() => setMenuOpen((prev) => !prev)}
                >
                    <span className={menuOpen ? "burgerLine open" : "burgerLine"}></span>
                    <span className={menuOpen ? "burgerLine open" : "burgerLine"}></span>
                    <span className={menuOpen ? "burgerLine open" : "burgerLine"}></span>
                </button>

                <AnimatePresence>
                    {(menuOpen || true) && (
                        <ul id="navLinks" className={menuOpen ? "navOpen" : ""}>
                            {NAV_LINKS.map((link) => (
                                <motion.li key={link.key}>
                                    <a
                                        data-key={link.key}
                                        onClick={(e) => handleClick(e, link.key, link.href)}
                                        className={clickedPage === link.key ? "link linkActive smooth-scroll" : "link smooth-scroll"}
                                        href={link.href}
                                    >
                                        {link.label}
                                    </a>
                                </motion.li>
                            ))}
                        </ul>
                    )}
                </AnimatePresence>

                <div id="navActions">
                    <a
                        id="resumeLink"
                        href="/resume.pdf"
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => setMenuOpen(false)}
                    >
                        Resume
                    </a>
                    <button
                        id="themeToggle"
                        aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                        onClick={toggleTheme}
                    >
                        {theme === "dark" ? <SunIcon /> : <MoonIcon />}
                    </button>
                </div>
            </div>
        </nav>
    );
}
